import type { Equipment } from '../../api/types'

export type Kind = 'text' | 'long' | 'decimal' | 'integer' | 'select'
export interface SpecField { key: string; label: string; kind: Kind; options?: [string, string][] }
export interface SpecSection { title: string; noun: string; fields: SpecField[] }

const f = (key: string, label: string, kind: Kind = 'text'): SpecField => ({ key, label, kind })

export const INSTALLATION_STATUSES: [string, string][] = [['unknown', 'Desconocido'], ['installed', 'Instalado'], ['dismounted', 'Desmontado'], ['stopped', 'Detenido']]

/** Scalar technical-spec columns, grouped and ordered like the equipment sheet ("FICHA DE INFORMACION"). */
export const SPEC_SECTIONS: SpecSection[] = [
  { title: 'General', noun: '', fields: [f('manufacturer', 'Fabricante'), f('model', 'Modelo'), f('serial_number', 'Serie del equipo'), f('capacity', 'Capacidad'), f('year', 'Año', 'integer'), f('internal_code', 'Código interno'), f('cost_center', 'Centro de costo'), f('driven_machine', 'Máquina accionada'), { key: 'installation_status', label: 'Estado de instalación', kind: 'select', options: INSTALLATION_STATUSES }] },
  { title: 'Reductor', noun: 'reductor', fields: [f('reducer_fabrication_order', 'Orden de fabricación'), f('reducer_type', 'Tipo'), f('reducer_brand', 'Marca'), f('reducer_reference', 'Referencia'), f('reducer_serial', 'Serie'), f('reducer_power_kw', 'Potencia (kW)', 'decimal'), f('reducer_power_hp', 'Potencia (Hp)', 'decimal'), f('reducer_ratio', 'Relación', 'decimal'), f('reducer_input_rpm', 'rpm de entrada', 'integer'), f('reducer_output_rpm', 'rpm de salida', 'decimal'), f('reducer_torque_nm', 'Torque nominal (Nm)', 'decimal'), f('reducer_service_factor', 'Factor de servicio', 'decimal'), f('reducer_efficiency_pct', 'Eficiencia (%)', 'decimal'), f('reducer_lubricant_spec', 'Lubricante'), f('reducer_oil_brand', 'Marca de aceite'), f('reducer_oil_volume_l', 'Volumen de aceite (L)', 'decimal'), f('reducer_grease_quantity_g', 'Cantidad de grasa (g)', 'decimal'), f('reducer_mount_position', 'Posición de montaje'), f('reducer_relube_frequency_hours', 'Frecuencia de reposición (h)', 'integer'), f('reducer_relube_frequency_text', 'Frecuencia de reposición (como figura en la ficha)'), f('reducer_parts_drawing_ref', 'Plano de partes'), f('reducer_other', 'Otros', 'long')] },
  { title: 'Motor', noun: 'motor', fields: [f('motor_brand', 'Marca'), f('motor_reference', 'Referencia'), f('motor_serial', 'Serie'), f('motor_flange_type', 'Tipo de brida'), f('motor_power_hp', 'Potencia (Hp)', 'decimal'), f('motor_power_kw', 'Potencia (kW)', 'decimal'), f('motor_power_text', 'Potencia (como figura en la placa)'), f('motor_rpm', 'rpm', 'integer'), f('motor_rpm_text', 'rpm (como figura en la placa)'), f('motor_voltage_v', 'Voltaje (V)', 'integer'), f('motor_voltage_text', 'Tensión (como figura en la placa)'), f('motor_current_a', 'Corriente (A)', 'decimal'), f('motor_current_text', 'Corriente (como figura en la placa)'), f('motor_frequency_hz', 'Frecuencia (Hz)', 'decimal'), f('motor_insulation_class', 'Aislamiento'), f('motor_protection', 'Protección (IP)'), f('motor_power_factor', 'Factor de potencia', 'decimal'), f('motor_efficiency_pct', 'Eficiencia (%)', 'decimal'), f('motor_efficiency_class', 'Clase de eficiencia (IE)'), f('motor_thermal_protection', 'Protección térmica'), f('motor_thermal_protection_capacity', 'Capacidad protección térmica'), f('motor_thermal_protection_setting', 'Calibrado protección térmica'), f('motor_magnetic_protection', 'Protección magnética'), f('motor_cable', 'Cable'), f('motor_fan_reference', 'Referencia ventilador'), f('motor_fan_outer_diameter_mm', 'Ø externo ventilador (mm)', 'decimal'), f('motor_fan_inner_diameter_mm', 'Ø interno ventilador (mm)', 'decimal'), f('motor_cowl_outer_diameter_mm', 'Ø externo caperuza (mm)', 'decimal'), f('motor_cowl_height_mm', 'Altura caperuza (mm)', 'decimal'), f('motor_parts_drawing_ref', 'Plano de partes'), f('motor_other', 'Otros', 'long')] },
]

export const TRANSMISSIONS: [string, string][] = [['chain', 'Cadena'], ['belt', 'Correa'], ['coupling', 'Acople'], ['other', 'Otro']]
export type TransmissionKind = 'chain' | 'belt' | 'coupling'
export const TRANSMISSION_KINDS: { key: TransmissionKind; title: string; noun: string; fields: [string, string][] }[] = [
  { key: 'chain', title: 'Cadena', noun: 'cadena', fields: [['reference', 'Referencia'], ['center_distance_mm', 'Distancia entre centros (mm)'], ['driver_pinion', 'Piñón conductor'], ['driver_inner_diameter_mm', 'Ø interno piñón conductor (mm)'], ['driver_keyway_mm', 'Cuñero piñón conductor (mm)'], ['driven_pinion', 'Piñón conducido'], ['driven_inner_diameter_mm', 'Ø interno piñón conducido (mm)'], ['driven_keyway_mm', 'Cuñero piñón conducido (mm)'], ['grease_brand', 'Marca de grasa'], ['grease_class', 'Clase de grasa'], ['grease_quantity_g', 'Cantidad de grasa (g)']] },
  { key: 'belt', title: 'Correas', noun: 'correas', fields: [['reference', 'Referencia'], ['center_distance_mm', 'Distancia entre centros (mm)'], ['driver_pulley_outer_diameter_mm', 'Ø ext. polea conductora (mm)'], ['driven_pulley_outer_diameter_mm', 'Ø ext. polea conducida (mm)'], ['driver_channels', 'Canales conductora'], ['driven_channels', 'Canales conducida'], ['driver_inner_diameter_mm', 'Ø interno conductora (mm)'], ['driven_inner_diameter_mm', 'Ø interno conducida (mm)'], ['driver_keyway_mm', 'Cuñero conductora (mm)'], ['driven_keyway_mm', 'Cuñero conducida (mm)']] },
  { key: 'coupling', title: 'Acople', noun: 'acople', fields: [['reference', 'Referencia'], ['driver_shaft_diameter_mm', 'Ø eje conductor (mm)'], ['driven_shaft_diameter_mm', 'Ø eje conducido (mm)'], ['driver_keyway_mm', 'Cuñero conductor (mm)'], ['driven_keyway_mm', 'Cuñero conducido (mm)']] },
]

type Row = Record<string, string>
type Details = Partial<Record<TransmissionKind, Record<string, unknown>>>

/** Chain details saved before each sprocket had its own bore / keyway: the single value belonged to the driver. */
export function normaliseDetails(details: unknown): Details {
  const d = { ...((details ?? {}) as Details) }
  const chain = d.chain
  if (chain) {
    d.chain = {
      ...chain,
      driver_inner_diameter_mm: chain.driver_inner_diameter_mm || chain.inner_diameter_mm || '',
      driver_keyway_mm: chain.driver_keyway_mm || chain.keyway_mm || '',
    }
  }
  return d
}

export type ListKey = 'bearings' | 'retainers' | 'pillow_blocks' | 'parts_list'
/** Mirrors the serializer caps (20 rows for bearings/retainers/chumaceras, 200 for the parts list). */
export const LIST_LIMITS = { rows: 20, parts: 200 }
export const LIST_COLUMNS: Record<ListKey, string[]> = {
  bearings: ['reference', 'fixing_sleeve'],
  retainers: ['reference'],
  pillow_blocks: ['reference'],
  parts_list: ['position', 'description', 'reference', 'quantity', 'sheet'],
}

export interface FormState {
  values: Record<string, string>
  hasBrake: boolean
  details: Record<TransmissionKind, Row>
  lists: Record<ListKey, Row[]>
}

const SCALARS: SpecField[] = [f('name', 'Nombre'), f('notes', 'Notas', 'long'), f('transmission_type', 'Tipo de transmisión'), ...SPEC_SECTIONS.flatMap((s) => s.fields)]
const KIND_OF = new Map(SCALARS.map((x) => [x.key, x.kind]))
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v))
const asRows = (v: unknown): Record<string, unknown>[] => (Array.isArray(v) ? (v as Record<string, unknown>[]) : [])
const isBlank = (row: Row) => Object.values(row).every((v) => (v ?? '').trim() === '')

export function toFormState(eq: Equipment): FormState {
  const source = eq as unknown as Record<string, unknown>
  const details = normaliseDetails(eq.transmission_details)
  return {
    values: Object.fromEntries(SCALARS.map((x) => [x.key, str(source[x.key])])),
    hasBrake: Boolean(eq.motor_has_brake),
    details: Object.fromEntries(TRANSMISSION_KINDS.map((k) => [k.key, Object.fromEntries(k.fields.map(([name]) => [name, str(details[k.key]?.[name])]))])) as FormState['details'],
    lists: Object.fromEntries((Object.keys(LIST_COLUMNS) as ListKey[]).map((key) => [key, asRows(source[key]).map((r) => Object.fromEntries(LIST_COLUMNS[key].map((c) => [c, str(r[c])])))])) as FormState['lists'],
  }
}

/** A blank equipment as the baseline of the creation form (the status starts as unknown, like the model). */
const EMPTY = { installation_status: 'unknown', motor_has_brake: false, transmission_details: {}, bearings: [], retainers: [], pillow_blocks: [], parts_list: [] } as unknown as Equipment

export function emptyFormState(): FormState {
  return toFormState(EMPTY)
}

const num = (raw: string) => Number(raw.trim().replace(',', '.'))
const isNumeric = (kind: Kind | undefined) => kind === 'decimal' || kind === 'integer'

function cleanRows(key: ListKey, rows: Row[]): Record<string, unknown>[] {
  return rows.filter((r) => !isBlank(r)).map((r) => Object.fromEntries(LIST_COLUMNS[key].flatMap((c) => {
    const raw = (r[c] ?? '').trim()
    if (c === 'sheet' && raw === '') return []  // only imported parts record their plano sheet
    return [[c, key === 'parts_list' && c === 'quantity' ? (raw === '' ? null : num(raw)) : raw]]
  })))
}

/** Local validation; the server stays the authority and its errors are shown too. */
export function validateForm(s: FormState): Record<string, string> {
  const errors: Record<string, string> = {}
  if (!s.values.name.trim()) errors.name = 'El nombre es obligatorio.'
  for (const [key, raw] of Object.entries(s.values)) {
    const kind = KIND_OF.get(key)
    if (!isNumeric(kind) || raw.trim() === '') continue
    const n = num(raw)
    if (!Number.isFinite(n)) errors[key] = 'Ingresar un número.'
    else if (kind === 'integer' && !Number.isInteger(n)) errors[key] = 'Ingresar un número entero.'
  }
  for (const key of Object.keys(LIST_COLUMNS) as ListKey[]) {
    const rows = s.lists[key].filter((r) => !isBlank(r))
    const max = key === 'parts_list' ? LIST_LIMITS.parts : LIST_LIMITS.rows
    if (rows.length > max) errors[key] = `Máximo ${max} filas.`
  }
  if (s.lists.parts_list.some((r) => (r.quantity ?? '').trim() !== '' && !Number.isInteger(num(r.quantity)))) errors.parts_list = 'La cantidad debe ser un número entero.'
  return errors
}

/** The PATCH body: only fields that differ from the loaded equipment. Assumes `validateForm` passed. */
export function buildPatch(eq: Equipment, s: FormState): Record<string, unknown> {
  const source = eq as unknown as Record<string, unknown>
  const patch: Record<string, unknown> = {}
  for (const x of SCALARS) {
    const raw = s.values[x.key].trim()
    const before = str(source[x.key])
    if (isNumeric(x.kind)) {
      const next = raw === '' ? null : num(raw)
      if (next !== (before === '' ? null : Number(before))) patch[x.key] = next
    } else if (raw !== before.trim()) patch[x.key] = raw
  }
  if (s.hasBrake !== Boolean(eq.motor_has_brake)) patch.motor_has_brake = s.hasBrake
  const before = toFormState(eq)
  const changedKinds = TRANSMISSION_KINDS.filter((k) => JSON.stringify(trimRow(s.details[k.key])) !== JSON.stringify(trimRow(before.details[k.key])))
  if (changedKinds.length) {
    // The column is replaced as a whole: send every kind so the ones not edited survive.
    patch.transmission_details = Object.fromEntries(TRANSMISSION_KINDS.map((k) => [k.key, filledOnly(s.details[k.key])]))
  }
  for (const key of Object.keys(LIST_COLUMNS) as ListKey[]) {
    const next = cleanRows(key, s.lists[key])
    if (JSON.stringify(next) !== JSON.stringify(cleanRows(key, before.lists[key]))) patch[key] = next
  }
  return patch
}

/** The POST body of a new equipment: every filled field of the full form (area and route-critical are added by the caller). */
export function buildCreatePayload(s: FormState): Record<string, unknown> {
  return buildPatch(EMPTY, s)
}

function trimRow(row: Row): Row {
  return Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v.trim()]))
}

function filledOnly(row: Row): Row {
  return Object.fromEntries(Object.entries(trimRow(row)).filter(([, v]) => v !== ''))
}
